import { useState } from 'react';
import useScrollReveal from '@/hooks/useScrollReveal';
import { homeFaqs } from '@/mocks/faq';

interface FAQSectionProps {
  faqs?: { question: string; answer: string }[];
  title?: string;
  subtitle?: string;
  theme?: 'light' | 'dark';
}

const FAQSection = ({
  faqs = homeFaqs,
  title = 'Frequently Asked Questions',
  subtitle = 'Voice Search Optimized Answers',
  theme = 'light',
}: FAQSectionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const ref = useScrollReveal();
  const isDark = theme === 'dark';

  return (
    <section className={`py-24 px-6 ${isDark ? 'bg-charcoal' : 'bg-white'}`}>
      <div ref={ref} className="reveal max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">
              {subtitle}
            </span>
            <div className="w-8 h-[2px] bg-gold" />
          </div>
          <h2 className={`font-playfair text-4xl md:text-5xl font-bold ${isDark ? 'text-white' : 'text-charcoal'}`}>
            {title}
          </h2>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`rounded-lg border transition-all duration-200 overflow-hidden ${
                isDark
                  ? openIndex === i ? 'border-gold/40 bg-white/5' : 'border-white/10 bg-white/3'
                  : openIndex === i ? 'border-gold/40 bg-cream' : 'border-cream-100 bg-cream'
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
              >
                <span className={`font-inter text-base font-semibold ${isDark ? 'text-white' : 'text-charcoal'}`}>
                  {faq.question}
                </span>
                <div className="w-7 h-7 flex items-center justify-center shrink-0">
                  <i className={`text-gold text-xl transition-transform duration-200 ${openIndex === i ? 'ri-subtract-line' : 'ri-add-line'}`} />
                </div>
              </button>
              {openIndex === i && (
                <div className="px-6 pb-5">
                  <p className={`font-inter text-sm leading-relaxed ${isDark ? 'text-white/60' : 'text-charcoal/65'}`}>
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
